/**
 * Finish grouping for the PDP finish step.
 *
 * There is no backend flag for finish tiers yet, so the tier is inferred from the finish name.
 * Grouping differs by material family: wood separates bare/clear coats from stains, metal
 * leads with bare brushed stainless, then the standard powder-coat tier, then the premium
 * coastal tier.
 *
 * Options arrive as either a plain finish name or `"Name:#hex"`; only the name is matched on.
 */

import { cleanOptionValue } from './optionAxes';

export type FinishGroup = {
  title: string;
  /** Raw option values, in the order they were handed in. */
  options: string[];
};

const WOOD_UNFINISHED = /unfinished/i;
const WOOD_CLEAR_OR_NATURAL = /natural|clear/i;
const METAL_BRUSHED = /brushed/i;
const STANDARD_POWDER_COAT_FINISHES = new Set(['standard black', 'standard silver', 'standard white']);

// Named stain families. Finishes are matched by exact name (case-insensitive) so a stain only
// ever lands in the family it was assigned to, never by a loose substring match.
const WOOD_PAINT = new Set(['satin black', 'satin white']);
const WOOD_NATURAL_TONES = new Set(['seashell', 'dune', 'wicker', 'fencerow']);
const WOOD_BALANCED_BROWNS = new Set(['flagstone', 'wheatfield', 'timber', 'loam']);
const WOOD_WARM_GRAYS = new Set(['tempest', 'boulder', 'slate', 'basalt']);
const WOOD_RED_BROWNS = new Set(['peatmoss', 'orchard', 'sequoia', 'terrace']);

/** Relative luminance (0–255) of a `#rrggbb` value; null when there is no parseable hex. */
const luminanceOf = (option: string): number | null => {
  const match = /#([0-9a-f]{6})/i.exec(option);
  if (!match) return null;
  const value = parseInt(match[1], 16);
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Darkest first, lightest last. Options without a hex sort to the end so a missing colour never
// jumps to the front of the swatch row.
const byLightnessAscending = (a: string, b: string) => {
  const aLum = luminanceOf(a);
  const bLum = luminanceOf(b);
  if (aLum === null) return bLum === null ? 0 : 1;
  if (bLum === null) return -1;
  return aLum - bLum;
};

type FinishGroupDefinition = {
  title: string;
  matches: (name: string) => boolean;
  /** Sort this group's swatches by colour lightness instead of keeping the incoming order. */
  sortByLightness?: boolean;
};

// Row order is merchandising order: bare wood first, then the stain families darkest-family to
// lightest, with Paint last because it is the only opaque option — it is a different kind of
// choice from a stain, not another palette.
const WOOD_GROUPS: FinishGroupDefinition[] = [
  { title: 'Unfinished', matches: (name) => WOOD_UNFINISHED.test(name) },
  { title: 'Clear Finished / Natural', matches: (name) => WOOD_CLEAR_OR_NATURAL.test(name) },
  { title: 'Natural Tones', matches: (name) => WOOD_NATURAL_TONES.has(name.toLowerCase()) },
  { title: 'Balanced Browns', matches: (name) => WOOD_BALANCED_BROWNS.has(name.toLowerCase()) },
  { title: 'Warm Grays', matches: (name) => WOOD_WARM_GRAYS.has(name.toLowerCase()) },
  { title: 'Red Browns', matches: (name) => WOOD_RED_BROWNS.has(name.toLowerCase()) },
  { title: 'Paint', matches: (name) => WOOD_PAINT.has(name.toLowerCase()) },
  { title: 'Stains', matches: () => true },
];

// Brushed leads: it is bare metal, not a coating, so it reads as the "no finish applied" choice
// the way Unfinished does for wood. It only ever appears on the stainless materials — the caller
// filters it out for Aluminum — so on an Aluminum variant this row is simply absent.
const METAL_GROUPS: FinishGroupDefinition[] = [
  { title: 'Brushed Stainless', matches: (name) => METAL_BRUSHED.test(name) },
  { title: 'Standard Powder Coat', matches: (name) => STANDARD_POWDER_COAT_FINISHES.has(name.toLowerCase()) },
  { title: 'Premium Coastal Grade Finishes', matches: () => true, sortByLightness: true },
];

/**
 * Splits finish options into display groups. Each option lands in the first group that matches it,
 * so the catch-all group must stay last. Groups that end up empty are dropped rather than rendered
 * as a bare heading.
 */
export const groupFinishOptions = (options: string[], isWoodProduct: boolean): FinishGroup[] => {
  const definitions = isWoodProduct ? WOOD_GROUPS : METAL_GROUPS;
  const buckets: string[][] = definitions.map(() => []);

  options.forEach((option) => {
    const name = cleanOptionValue(option);
    const index = definitions.findIndex((definition) => definition.matches(name));
    buckets[index === -1 ? definitions.length - 1 : index].push(option);
  });

  return definitions
    .map((definition, index) => ({
      title: definition.title,
      options: definition.sortByLightness ? [...buckets[index]].sort(byLightnessAscending) : buckets[index],
    }))
    .filter((group) => group.options.length > 0);
};

export const BRUSHED_FINISH = 'Brushed Stainless';
export const ALUMINUM_MATERIAL = 'Aluminum';

/**
 * Finish options ("Name:#hex") a variant of this material can actually take: Brushed Stainless is a
 * stainless-only finish, so it is dropped for Aluminum. Shared by the buy-box picker and the gallery's
 * swatch chips so the two controls can never offer different finishes.
 */
export const compatibleFinishOptions = (options: string[], material?: string) =>
  material === ALUMINUM_MATERIAL ? options.filter((option) => option.split(':')[0] !== BRUSHED_FINISH) : options;
